import { useEffect, useRef } from 'react'
import { useEffectEvent } from '@funkit/connect-core'

import { FiatRegionUnsupportedScreen } from '../fiat/FiatRegionUnsupportedScreen'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import type { DepositFlowCallbacks } from './depositFlowConfig'
import { flowError } from './flowError'

/**
 * The region refusal raised before a deposit has started.
 *
 * The host hears it on the deposit's own error channel rather than
 * `onCheckoutBlocked`: that one says the user is barred from checkout, and a
 * corridor we do not serve is a different fact.
 */
export function FiatRegionBlockedStep({
  checkoutId,
  callbacks,
  onClose,
}: {
  checkoutId: string
  callbacks: Pick<DepositFlowCallbacks, 'onDepositError'>
  onClose: () => void
}) {
  const { t } = useFunkitTranslation()
  // One refusal per mounted screen. `useEffectEvent` keeps the callback stable
  // but does not stop React replaying the effect, which a Strict Mode host
  // does on every mount — and the host would hear the same block twice.
  const reported = useRef(false)
  const report = useEffectEvent(() => {
    if (reported.current) {
      return
    }
    reported.current = true
    logger.info('connect-rn:deposit:region_blocked', { checkoutId })
    callbacks.onDepositError?.(
      flowError('blocked', t('fiat.regionUnsupportedTitle')),
    )
  })
  useEffect(() => {
    report()
  }, [])
  return <FiatRegionUnsupportedScreen onClose={onClose} />
}
